import React, { useState, useEffect } from 'react';
import { Grid, DollarSign, Package, Users, TrendingUp, Download, X, DownloadCloud, BarChart2 } from 'react-feather';
import './MesKpi.css';
import { Clock } from 'lucide-react';
import axios from 'axios';
import { base_api_url } from "../../../environment.jsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Line, Doughnut } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler,
  ArcElement
);


const MesKpi = () => {
    const [activeTab, setActiveTab] = useState('vue_ensemble');
    const [timeFilter, setTimeFilter] = useState('today');
    const [showRapport, setShowRapport] = useState(false);
    const [kpiData, setKpiData] = useState(null);
    const [, setLoadingKpi] = useState(false); 
    const [kpiProduitData, setKpiProduitData] = useState(null);
    const [, setLoadingProduitKpi] = useState(false);
    const nowDate = new Date();
    const [produitYear, setProduitYear] = useState(nowDate.getFullYear());
    const [produitMonth, setProduitMonth] = useState(nowDate.getMonth() + 1);
    const [loadingProduits, setLoadingProduits] = useState(false);
    const [sortBy, setSortBy] = useState('quantite'); // 'quantite' | 'montant'
    const MySwal = withReactContent(Swal);
    const [devise, setDevise] = useState();

    const tabs = [
        { id: 'vue_ensemble', label: "Vue d'ensemble", icon: Grid },
        { id: 'finances', label: "Finances & Marges", icon: DollarSign },
        { id: 'produits', label: "Performance Produits", icon: Package },
        { id: 'rh', label: "Ressources Humaines", icon: Users },
    ];

    const periods = [
        { label: "Aujourd'hui", value: 'today' },
        { label: "Cette semaine", value: 'hebdo' },
        { label: "Ce mois", value: 'mois'},
        { label: "Cette année", value: 'annee'},
    ];


      // Déterminer la granularité d'affichage en fonction du nombre de jours
    const getChartGranularity = () => {
        if (!kpiData?.evolution_ventes || kpiData.evolution_ventes.length === 0) {
            return 'jour';
        }

        const nbJours = kpiData.evolution_ventes.length;
        
        if (nbJours <= 1) return 'heure';        // Aujourd'hui
        if (nbJours <= 7) return 'jour';         // Semaine
        if (nbJours <= 31) return 'semaine';      // Mois
        return 'mois';                             // Année
    };

    // Formater les dates selon la granularité
    const formatDate = (dateStr, granularity) => {
        const date = new Date(dateStr);
        
        switch(granularity) {
            case 'heure':
                return `${date.getHours()}h`;
            case 'jour':
                return date.toLocaleDateString('fr-FR', { weekday: 'short' });
            case 'semaine':
                return `Sem ${Math.ceil(date.getDate() / 7)}`;
            case 'mois':
                return date.toLocaleDateString('fr-FR', { month: 'short' });
            default:
                return dateStr;
        }
    };

     // Générer les labels complets pour le tooltip
    const getFullLabel = (dateStr, granularity) => {
        const date = new Date(dateStr);
        
        switch(granularity) {
            case 'heure':
                return date.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            case 'jour':
                return date.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long' 
                });
            case 'semaine':
                return `Semaine du ${date.toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'long' 
                })}`;
            case 'mois':
                return date.toLocaleDateString('fr-FR', { 
                    month: 'long', 
                    year: 'numeric' 
                });
            default:
                return dateStr;
        }
    };
     
   /*  const getChartData = () => {
        if (!kpiData?.evolution_ventes) return { labels: [], datasets: [] };
         let filteredData = [...kpiData.evolution_ventes];

        const labels = filteredData.map(item => item.date);
        const data = filteredData.map(item => item.ventes);

        return {
            labels,
            datasets: [
                {
                    label: "Ventes",
                    data,
                    fill: false,
                    backgroundColor: '#e85a1f',
                    borderColor: '#e85a1f',
                    tension: 0.3, // pour une ligne lisse
                }
            ]
        };
    }; */

    const getChartData = () => {
        if (!kpiData?.evolution_ventes || kpiData.evolution_ventes.length === 0) {
            return {
                labels: [],
                datasets: [{
                    label: "Ventes",
                    data: [],
                    fill: true,
                    backgroundColor: 'rgba(232, 90, 31, 0.1)',
                    borderColor: '#e85a1f',
                    borderWidth: 3,
                    pointBackgroundColor: '#e85a1f',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    tension: 0.3,
                }]
            };
        }

        const granularity = getChartGranularity();
        
        // Trier les données par date
        const sortedData = [...kpiData.evolution_ventes].sort((a, b) => 
            new Date(a.date) - new Date(b.date)
        );

        const labels = sortedData.map(item => formatDate(item.date, granularity));
        const data = sortedData.map(item => item.ventes);

        // Calculer la moyenne mobile (lissée sur 3 points)
        const moyenneMobile = data.map((_, index, arr) => {
            const start = Math.max(0, index - 1);
            const end = Math.min(arr.length, index + 2);
            const slice = arr.slice(start, end);
            return slice.reduce((a, b) => a + b, 0) / slice.length;
        });

        return {
            labels,
            datasets: [
                {
                    label: "Ventes",
                    data,
                    fill: true,
                    backgroundColor: 'rgba(232, 90, 31, 0.1)',
                    borderColor: '#e85a1f',
                    borderWidth: 3,
                    pointBackgroundColor: '#e85a1f',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    tension: 0.3,
                },
                {
                    label: "Tendance",
                    data: moyenneMobile,
                    fill: false,
                    borderColor: 'rgba(75, 192, 192, 0.8)',
                    borderWidth: 2,
                    borderDash: [5, 5],
                    pointRadius: 0,
                    tension: 0.4,
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    boxWidth: 6,
                    font: { size: 12 }
                }
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (tooltipItems) => {
                        if (!kpiData?.evolution_ventes) return '';
                        const index = tooltipItems[0].dataIndex;
                        const item = kpiData.evolution_ventes[index];
                        return getFullLabel(item.date, getChartGranularity());
                    },
                    label: (context) => {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += context.parsed.y + ' ventes';
                        }
                        return label;
                    }
                },
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#e85a1f',
                borderWidth: 1,
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    font: { size: 11 },
                    maxRotation: 45,
                    minRotation: 45,
                }
            },
            y: {
                beginAtZero: true,
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                ticks: {
                    callback: function(value) {
                        return value + ' ventes';
                    },
                    font: { size: 11 },
                    stepSize: 1, // Pour les nombres entiers (ventes)
                }
            }
        },
        elements: {
            line: { tension: 0.3 }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };


    const renderVueEnsemble = () => (
        <div className="kpi-tab-content">
            {/* Time Filters */}
            <div className="kpi-time-filters">
                {periods.map((p) => (
                    <button 
                        key={p.value}
                        className={`kpi-filter-btn ${timeFilter === p.value ? 'active' : ''}`}
                        onClick={() => setTimeFilter(p.value)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {/* KPI Cards */}
            <div className="kpi-cards-grid">
                {/* CA */}
                <div className="kpi-card">
                    <div className="kpi-card-header">
                        <div className="kpi-icon-wrapper kpi-bg-orange">
                            <TrendingUp size={16} className="kpi-color-orange" />
                        </div>
                        <span className="kpi-badge kpi-badge-green">+12%</span>
                    </div>
                    <div className="kpi-card-body">
                        <h3 className="kpi-value">
                            {kpiData?.statistiques_globales?.chiffre_affaires
                                ? kpiData.statistiques_globales.chiffre_affaires.toFixed(2)
                                : "0.00"}{devise}
                        </h3>
                        <p className="kpi-label">CHIFFRE D&apos;AFFAIRES</p>
                    </div>
                </div>

                {/* Ticket Moyen */}
                <div className="kpi-card">
                    <div className="kpi-card-header">
                        <div className="kpi-icon-wrapper kpi-bg-blue">
                            <DollarSign size={16} className="kpi-color-blue" />
                        </div>
                        <span className="kpi-badge kpi-badge-red">-2%</span>
                    </div>
                    <div className="kpi-card-body">
                        <h3 className="kpi-value">{kpiData?.statistiques_globales?.ticket_moyen != null 
                            ? kpiData.statistiques_globales.ticket_moyen.toFixed(2) 
                            : "0.00"}{devise}
                        </h3>
                        <p className="kpi-label">TICKET MOYEN</p>
                    </div>
                </div>

                {/* Taux de Remplissage */}
                <div className="kpi-card">
                    <div className="kpi-card-header">
                        <div className="kpi-icon-wrapper kpi-bg-purple">
                            <Users size={16} className="kpi-color-purple" />
                        </div>
                        <span className="kpi-badge kpi-badge-neutral">Stable</span>
                    </div>
                    <div className="kpi-card-body">
                        <h3 className="kpi-value">85%</h3>
                        <p className="kpi-label">TAUX DE REMPLISSAGE</p>
                    </div>
                </div>
            </div>

            {/* Chart Area */}
            <div className="kpi-chart-section">
                <div className="kpi-chart-header">
                    <div className="kpi-chart-title-group">
                        <h3 className="kpi-chart-title">Évolution du CA</h3>
                      {/*    <div className="kpi-chart-toggle">
                            {['Semaine', 'Mois', 'Année'].map((p)=> (
                                <button 
                                    key={p}
                                    className={`kpi-toggle-btn ${chartPeriod === p ? 'active' : ''}`}
                                    onClick={() => setChartPeriod(p)}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>  */}
                    </div>
                    <button className="kpi-export-btn">
                        <Download size={14} />
                        Exporter
                    </button>
                </div>
                
                <div className="kpi-chart-placeholder">

                <div className="kpi-chart-placeholder" style={{ height: '350px', position: 'relative' }}>
                    {kpiData?.evolution_ventes && kpiData.evolution_ventes.length > 0 ? (
                        <Line data={getChartData()} options={chartOptions} />
                    ) : (
                        <div className="kpi-no-data">
                            <p>Aucune donnée de vente pour cette période</p>
                        </div>
                    )}
                </div>

               
                {/*                     
                    <Line
                        data={getChartData()}
                        options={{
                            responsive: true,
                            plugins: {
                                legend: { display: false },
                                tooltip: {
                                    enabled: true,
                                    callbacks: {
                                        label: (tooltipItem) => `Ventes : ${tooltipItem.raw} €`
                                    }
                                }
                            },
                            scales: {
                                x: {
                                    title: { display: true, text: 'Date' }
                                },
                                y: {
                                    title: { display: true, text: 'Ventes' },
                                    beginAtZero: true
                                }
                            }
                        }}
                   /> */}
                   {/*   <div className="kpi-chart-placeholder">
                        {chartPeriod === 'Semaine' && (
                            <div className="kpi-axis-x">
                                <span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span>
                            </div>
                        )}
                        {chartPeriod === 'Mois' && (
                            <div className="kpi-axis-x">
                                <span>Sem 1</span><span>Sem 2</span><span>Sem 3</span><span>Sem 4</span>
                            </div>
                        )}
                        {chartPeriod === 'Année' && (
                            <div className="kpi-axis-x">
                                <span>J</span><span>F</span><span>M</span><span>A</span><span>M</span><span>J</span><span>J</span><span>A</span><span>S</span><span>O</span><span>N</span><span>D</span>
                            </div>
                        )}
                    </div>  */}
                </div>
            </div>
          </div>
    );

    const renderFinances = () => (
        <div className="kpi-tab-content">
            {/* 4 KPIs Grid */}
            <div className="kpi-cards-grid kpi-cards-grid-4">
                {/* Marge Brute */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">MARGE BRUTE</p>
                            <h3 className="kpi-value-small">72%</h3>
                            <span className="kpi-obj">Obj. 70%</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-green-light">
                            <BarChart2 size={16} className="kpi-color-green" />
                        </div>
                    </div>
                </div>

                {/* Coût Matière */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">COÛT MATIÈRE (FOOD COST)</p>
                            <h3 className="kpi-value-small">28%</h3>
                            <span className="kpi-obj">Obj. &lt;30%</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-red-light">
                            <Package size={16} className="kpi-color-red" />
                        </div>
                    </div>
                </div>

                {/* Ratio Personnel */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">RATIO PERSONNEL</p>
                            <h3 className="kpi-value-small">31%</h3>
                            <span className="kpi-obj">Obj. 30%</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-blue-light">
                            <Users size={16} className="kpi-color-blue" />
                        </div>
                    </div>
                </div>

                {/* EBITDA */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">EBITDA (EST.)</p>
                            <h3 className="kpi-value-small">18%</h3>
                            <span className="kpi-obj">&gt; 15%</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-orange">
                            <TrendingUp size={16} className="kpi-color-orange" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Area: P&L Flash and Analysis */}
            <div className="kpi-main-grid">
                {/* P&L Flash */}
                <div className="kpi-section-card kpi-section-pnl">
                    <h3 className="kpi-section-title kpi-title-icon">
                        <div className="kpi-icon-inline kpi-fill-orange">
                            <DollarSign size={14} className="kpi-color-white" />
                        </div>
                        P&L Flash (Est. Hebdo)
                    </h3>
                    
                    <div className="kpi-pnl-table">
                        <div className="kpi-pnl-row">
                            <span className="kpi-pnl-label">Chiffre d&apos;Affaires HT</span>
                            <div className="kpi-pnl-values">
                                <span className="kpi-pnl-amount">13 250 €</span>
                                <span className="kpi-pnl-percent">100%</span>
                            </div>
                        </div>
                        <div className="kpi-pnl-row">
                            <span className="kpi-pnl-label">Coût Matière</span>
                            <div className="kpi-pnl-values">
                                <span className="kpi-pnl-amount kpi-text-red">- 3 710 €</span>
                                <span className="kpi-pnl-percent">28%</span>
                            </div>
                        </div>
                        <div className="kpi-pnl-row kpi-pnl-bold">
                            <span className="kpi-pnl-label">Marge Brute</span>
                            <div className="kpi-pnl-values">
                                <span className="kpi-pnl-amount">9 540 €</span>
                                <span className="kpi-pnl-percent">72%</span>
                            </div>
                        </div>
                        <div className="kpi-pnl-row">
                            <span className="kpi-pnl-label">Frais de Personnel</span>
                            <div className="kpi-pnl-values">
                                <span className="kpi-pnl-amount kpi-text-red">- 4 107 €</span>
                                <span className="kpi-pnl-percent">31%</span>
                            </div>
                        </div>
                        <div className="kpi-pnl-row">
                            <span className="kpi-pnl-label">Frais Généraux</span>
                            <div className="kpi-pnl-values">
                                <span className="kpi-pnl-amount kpi-text-red">- 1 325 €</span>
                                <span className="kpi-pnl-percent">10%</span>
                            </div>
                        </div>
                        <div className="kpi-pnl-row kpi-pnl-highlight">
                            <span className="kpi-pnl-label">Résultat Flash (EBE)</span>
                            <div className="kpi-pnl-values">
                                <span className="kpi-pnl-amount">4 108 €</span>
                                <span className="kpi-pnl-percent">31%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Analyse de Rentabilité */}
                <div className="kpi-section-card kpi-analysis-card">
                    <div className="kpi-analysis-content">
                        <div className="kpi-analysis-icon-lg">
                            <BarChart2 size={28} className="kpi-color-blue" />
                        </div>
                        <h4 className="kpi-analysis-title">Analyse de Rentabilité</h4>
                        <p className="kpi-analysis-text">
                            Vos coûts matière sont excellents cette semaine (28%). Attention cependant au ratio personnel qui dépasse légèrement l&apos;objectif en soirée.
                        </p>
                        <button className="kpi-btn-outline-orange" onClick={() => setShowRapport(true)}>Consulter le rapport complet</button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderProduits = () => {
        const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
        const produits     = kpiProduitData?.produits;
        const rawArticles  = produits?.top_articles || [];
        const topArticles  = [...rawArticles].sort((a, b) =>
            sortBy === 'montant'
                ? (b.prix_total || 0) - (a.prix_total || 0)
                : (b.quantite  || 0) - (a.quantite  || 0)
        );
        const ventesCanal  = produits?.ventes_par_canal || [];
        const perfServices = produits?.performances_par_service || [];
        const maxVal       = topArticles.length > 0
            ? Math.max(...topArticles.map(a => sortBy === 'montant' ? (a.prix_total || 0) : (a.quantite || 0)))
            : 1;
        const totalCanal   = ventesCanal.reduce((sum, c) => sum + (c.amount || 0), 0);
        const currentDate  = new Date();
        const isFuture     = produitYear > currentDate.getFullYear() ||
                             (produitYear === currentDate.getFullYear() && produitMonth >= currentDate.getMonth() + 1);
        const fmtAmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') + ' ' + (devise || '') : 'N/A';
        const RANK_COLORS  = ['#f59e0b','#9ca3af','#b45309'];

        const goBack = () => {
            if (produitMonth === 1) { setProduitYear(y => y - 1); setProduitMonth(12); }
            else setProduitMonth(m => m - 1);
        };
        const goNext = () => {
            if (isFuture) return;
            if (produitMonth === 12) { setProduitYear(y => y + 1); setProduitMonth(1); }
            else setProduitMonth(m => m + 1);
        };

        return (
            <div className="kpi-tab-content">

                {/* ── Sélecteur Mois / Année ── */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, marginBottom:20, padding:'10px 16px', backgroundColor:'white', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)', border:'1px solid #f1f5f9' }}>
                    <button onClick={goBack} style={{ width:32, height:32, borderRadius:'50%', border:'1px solid #e2e8f0', background:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, color:'#64748b' }}>‹</button>
                    <div style={{ textAlign:'center', minWidth:140 }}>
                        <div style={{ fontWeight:700, fontSize:15, color:'#1e293b' }}>{MONTHS[produitMonth - 1]}</div>
                        <div style={{ fontSize:12, color:'#94a3b8' }}>{produitYear}</div>
                    </div>
                    <button onClick={goNext} disabled={isFuture} style={{ width:32, height:32, borderRadius:'50%', border:'1px solid #e2e8f0', background:'white', cursor: isFuture ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, color: isFuture ? '#cbd5e1' : '#64748b' }}>›</button>
                </div>

                {loadingProduits ? (
                    /* ── Skeleton ── */
                    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                        {[1,2,3,4].map(i => (
                            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', backgroundColor:'white', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                                <div style={{ width:44, height:44, borderRadius:12, backgroundColor:'#f1f5f9', flexShrink:0, animation:'kpi-pulse 1.5s ease infinite' }} />
                                <div style={{ flex:1 }}>
                                    <div style={{ height:12, width:'55%', borderRadius:6, backgroundColor:'#f1f5f9', marginBottom:8, animation:'kpi-pulse 1.5s ease infinite' }} />
                                    <div style={{ height:10, width:'35%', borderRadius:6, backgroundColor:'#f1f5f9', animation:'kpi-pulse 1.5s ease infinite' }} />
                                </div>
                            </div>
                        ))}
                        <style>{`@keyframes kpi-pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
                    </div>
                ) : (
                    <>
                        {/* ── Grid responsive ── */}
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16, marginBottom:16 }}>

                            {/* ── Top Articles ── */}
                            <div style={{ backgroundColor:'white', borderRadius:16, padding:'20px', boxShadow:'0 1px 6px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
                                {/* Header avec toggle de tri */}
                                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
                                    <h3 style={{ fontSize:14, fontWeight:700, color:'#1e293b', display:'flex', alignItems:'center', gap:6, margin:0 }}>🏆 Top Articles</h3>
                                    <div style={{ display:'flex', gap:4, backgroundColor:'#f1f5f9', borderRadius:8, padding:3 }}>
                                        <button
                                            onClick={() => setSortBy('quantite')}
                                            style={{ fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:6, border:'none', cursor:'pointer', transition:'all 0.2s ease',
                                                backgroundColor: sortBy === 'quantite' ? 'white' : 'transparent',
                                                color: sortBy === 'quantite' ? '#e85a1f' : '#94a3b8',
                                                boxShadow: sortBy === 'quantite' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none'
                                            }}
                                        >📦 Qté vendue</button>
                                        <button
                                            onClick={() => setSortBy('montant')}
                                            style={{ fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:6, border:'none', cursor:'pointer', transition:'all 0.2s ease',
                                                backgroundColor: sortBy === 'montant' ? 'white' : 'transparent',
                                                color: sortBy === 'montant' ? '#e85a1f' : '#94a3b8',
                                                boxShadow: sortBy === 'montant' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none'
                                            }}
                                        >💰 Montant</button>
                                    </div>
                                </div>
                                {topArticles.length === 0 ? (
                                    <div style={{ textAlign:'center', padding:'32px 0', color:'#94a3b8' }}>
                                        <Package size={28} style={{ margin:'0 auto 8px', display:'block', opacity:0.3 }} />
                                        <p style={{ fontSize:12 }}>Aucun article vendu ce mois</p>
                                    </div>
                                ) : topArticles.map((article, idx) => (
                                    <div key={article.id_article || idx} style={{ display:'flex', flexDirection:'row', alignItems:'center', gap:10, padding:'10px 0', borderBottom: idx < topArticles.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                                        {/* Rang */}
                                        <div style={{ width:22, height:22, borderRadius:'50%', backgroundColor: RANK_COLORS[idx] || '#e5e7eb', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'white', flexShrink:0 }}>{idx + 1}</div>
                                        {/* Image */}
                                        {article.image_url && !article.image_url.includes('image de') ? (
                                            <img src={article.image_url} alt={article.nom} style={{ width:38, height:38, borderRadius:10, objectFit:'cover', flexShrink:0 }} onError={(e) => { e.target.style.display='none'; }} />
                                        ) : (
                                            <div style={{ width:38, height:38, borderRadius:10, backgroundColor:'#fff7ed', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>🍽️</div>
                                        )}
                                        {/* Infos */}
                                        <div style={{ flex:1, minWidth:0 }}>
                                            <div style={{ fontWeight:600, fontSize:13, color:'#1e293b', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{article.nom}</div>
                                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:2 }}>
                                                <span style={{ fontSize:11, color:'#94a3b8' }}>{article.quantite} vendus</span>
                                                <span style={{ fontSize:11, fontWeight:600, color:'#e85a1f' }}>{fmtAmt(article.prix_total)}</span>
                                            </div>
                                            <div style={{ height:4, backgroundColor:'#f1f5f9', borderRadius:4, marginTop:5, overflow:'hidden' }}>
                                                <div style={{ height:'100%', width:(((sortBy === 'montant' ? article.prix_total : article.quantite) / maxVal) * 100) + '%', background:'linear-gradient(90deg,#e85a1f,#f97316)', borderRadius:4, transition:'width 0.6s ease' }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ── Ventes par Canal (Doughnut Chart.js) ── */}
                            <div style={{ backgroundColor:'white', borderRadius:16, padding:'20px', boxShadow:'0 1px 6px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
                                <h3 style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>📡 Répartition Canaux</h3>
                                {ventesCanal.length === 0 ? (
                                    <div style={{ textAlign:'center', padding:'32px 0', color:'#94a3b8', fontSize:12 }}>Aucune donnée</div>
                                ) : (
                                    <>
                                        <div style={{ position:'relative', height:200, marginBottom:16 }}>
                                            <Doughnut
                                                data={{
                                                    labels: ventesCanal.map(c => c.label),
                                                    datasets: [{
                                                        data: ventesCanal.map(c => c.amount),
                                                        backgroundColor: ['#e85a1f','#3b82f6','#10b981','#8b5cf6'],
                                                        borderColor: ['#fff','#fff','#fff','#fff'],
                                                        borderWidth: 3,
                                                        hoverOffset: 8,
                                                    }]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    cutout: '68%',
                                                    plugins: {
                                                        legend: { display: false },
                                                        tooltip: {
                                                            enabled: true,
                                                            callbacks: {
                                                                label: (ctx) => {
                                                                    const pct = totalCanal > 0 ? ((ctx.parsed / totalCanal) * 100).toFixed(1) : 0;
                                                                    return ` ${ctx.label} : ${Number(ctx.parsed).toLocaleString('fr-FR')} ${devise || ''} (${pct}%)`;
                                                                }
                                                            },
                                                            backgroundColor:'rgba(15,23,42,0.85)',
                                                            titleColor:'#fff',
                                                            bodyColor:'#e2e8f0',
                                                            padding:10,
                                                            cornerRadius:8,
                                                        }
                                                    }
                                                }}
                                            />
                                            {/* Centre overlay */}
                                            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center', pointerEvents:'none' }}>
                                                <div style={{ fontSize:10, fontWeight:600, color:'#94a3b8', letterSpacing:'0.5px', textTransform:'uppercase' }}>Total</div>
                                                <div style={{ fontSize:16, fontWeight:800, color:'#1e293b', lineHeight:1.1 }}>{Number(totalCanal).toLocaleString('fr-FR')}</div>
                                                <div style={{ fontSize:10, color:'#94a3b8' }}>{devise}</div>
                                            </div>
                                        </div>
                                        {/* Légende */}
                                        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                            {ventesCanal.map((canal, idx) => {
                                                const DOT_COLORS = ['#e85a1f','#3b82f6','#10b981','#8b5cf6'];
                                                const pct = totalCanal > 0 ? ((canal.amount / totalCanal) * 100).toFixed(1) : 0;
                                                return (
                                                    <div key={idx} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 10px', backgroundColor:'#f8fafc', borderRadius:8 }}>
                                                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                                            <div style={{ width:10, height:10, borderRadius:'50%', backgroundColor: DOT_COLORS[idx % DOT_COLORS.length], flexShrink:0 }} />
                                                            <span style={{ fontSize:12, color:'#475569' }}>{canal.label}</span>
                                                        </div>
                                                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                                                            <span style={{ fontSize:12, fontWeight:700, color:'#1e293b' }}>{pct}%</span>
                                                            <span style={{ fontSize:10, color:'#94a3b8' }}>{fmtAmt(canal.amount)}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ── Performance par Service (cards responsives) ── */}
                        <div style={{ backgroundColor:'white', borderRadius:16, padding:'20px', boxShadow:'0 1px 6px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
                            <h3 style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>⏰ Performance par Service</h3>
                            {perfServices.length === 0 ? (
                                <div style={{ textAlign:'center', padding:'24px', color:'#94a3b8', fontSize:12 }}>Aucun service pour cette période</div>
                            ) : (
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12 }}>
                                    {perfServices.map((service, idx) => (
                                        <div key={service.nom_service || idx} style={{ background:'linear-gradient(135deg,#fff7ed,#fff)', border:'1px solid #fed7aa', borderRadius:12, padding:'14px 16px' }}>
                                            <div style={{ fontSize:12, fontWeight:700, color:'#c2410c', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.4px' }}>{service.nom_service}</div>
                                            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                                    <span style={{ fontSize:10, color:'#94a3b8' }}>CA</span>
                                                    <span style={{ fontSize:12, fontWeight:700, color:'#1e293b' }}>{fmtAmt(service.chiffre_affaires)}</span>
                                                </div>
                                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                                    <span style={{ fontSize:10, color:'#94a3b8' }}>Commandes</span>
                                                    <span style={{ fontSize:13, fontWeight:800, color:'#e85a1f' }}>{service.nb_commandes}</span>
                                                </div>
                                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:6, borderTop:'1px dashed #fed7aa' }}>
                                                    <span style={{ fontSize:10, color:'#94a3b8' }}>Panier moyen</span>
                                                    <span style={{ fontSize:12, fontWeight:700, color:'#1e293b' }}>{fmtAmt(service.panier_moyen)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderRH = () => (
        <div className="kpi-tab-content">
            {/* 4 KPIs Grid - RH */}
            <div className="kpi-cards-grid kpi-cards-grid-4">
                {/* Ratio Masse Salariale */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">RATIO MASSE SALARIALE</p>
                            <h3 className="kpi-value-small">31%</h3>
                            <span className="kpi-obj">Obj. 30%</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-blue-light">
                            <DollarSign size={16} className="kpi-color-blue" />
                        </div>
                    </div>
                </div>

                {/* Turn-Over */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">TURN-OVER (MENSUEL)</p>
                            <h3 className="kpi-value-small">5%</h3>
                            <span className="kpi-obj">&lt; 10%</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-orange-light">
                            <TrendingUp size={16} className="kpi-color-orange" />
                        </div>
                    </div>
                </div>

                {/* Heures Supp */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">HEURES SUPP. (SEMAINE)</p>
                            <h3 className="kpi-value-small">12h</h3>
                            <span className="kpi-obj">-2h vs N-1</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-purple-light">
                            <Clock size={16} className="kpi-color-purple" />
                        </div>
                    </div>
                </div>

                {/* Climat Social */}
                <div className="kpi-card kpi-card-compact">
                    <div className="kpi-card-header kpi-header-no-margin">
                        <div>
                            <p className="kpi-label">CLIMAT SOCIAL (ENPS)</p>
                            <h3 className="kpi-value-small">4.2/5</h3>
                            <span className="kpi-obj">Stable</span>
                        </div>
                        <div className="kpi-icon-wrapper kpi-bg-red-light">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="kpi-color-red">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Area: Performance Equipe and Repartition RH */}
            <div className="kpi-main-grid kpi-mt-24">
                {/* Performance Équipe */}
                <div className="kpi-section-card">
                    <h3 className="kpi-section-title kpi-mb-24">Performance Équipe</h3>
                    <div className="kpi-service-table-wrapper">
                        <table className="kpi-service-table kpi-team-table">
                            <thead>
                                <tr>
                                    <th>COLLABORATEUR</th>
                                    <th>RÔLE</th>
                                    <th>HEURES</th>
                                    <th>VENTES (CA)</th>
                                    <th>NOTE (AVIS)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="kpi-td-bold">Sophie Martin</td>
                                    <td className="kpi-text-gray">Serveur</td>
                                    <td className="kpi-td-bold">35h</td>
                                    <td className="kpi-td-bold">4 250 €</td>
                                    <td className="kpi-text-green-bold">4.9</td>
                                </tr>
                                <tr>
                                    <td className="kpi-td-bold">Marc Weber</td>
                                    <td className="kpi-text-gray">Barman</td>
                                    <td className="kpi-td-bold">25h</td>
                                    <td className="kpi-td-bold">2 100 €</td>
                                    <td className="kpi-text-green-bold">4.7</td>
                                </tr>
                                <tr>
                                    <td className="kpi-td-bold">Julie Pierre</td>
                                    <td className="kpi-text-gray">Serveur</td>
                                    <td className="kpi-td-bold">35h</td>
                                    <td className="kpi-td-bold">3 890 €</td>
                                    <td className="kpi-text-green-bold">4.5</td>
                                </tr>
                                <tr>
                                    <td className="kpi-td-bold">Thomas Dubois</td>
                                    <td className="kpi-text-gray">Cuisinier</td>
                                    <td className="kpi-td-bold">39h</td>
                                    <td className="kpi-text-gray">-</td>
                                    <td className="kpi-text-green-bold">4.8</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Répartition Coûts RH */}
                <div className="kpi-section-card kpi-rh-dist-card">
                    <h3 className="kpi-section-title kpi-mb-24">Répartition Coûts RH</h3>
                    
                    <div className="kpi-rh-bars">
                        {/* Salaires Fixes */}
                        <div className="kpi-rh-bar-wrap">
                            <div className="kpi-rh-bar-header">
                                <span className="kpi-rh-bar-label">Salaires Fixes</span>
                                <span className="kpi-rh-bar-pct">85%</span>
                            </div>
                            <div className="kpi-rh-progress-bg">
                                <div className="kpi-rh-progress-fill kpi-bg-blue" style={{ width: '85%' }}></div>
                            </div>
                        </div>

                        {/* Variables & Primes */}
                        <div className="kpi-rh-bar-wrap">
                            <div className="kpi-rh-bar-header">
                                <span className="kpi-rh-bar-label">Variables & Primes</span>
                                <span className="kpi-rh-bar-pct">10%</span>
                            </div>
                            <div className="kpi-rh-progress-bg">
                                <div className="kpi-rh-progress-fill kpi-bg-green" style={{ width: '10%' }}></div>
                            </div>
                        </div>

                        {/* Extras */}
                        <div className="kpi-rh-bar-wrap">
                            <div className="kpi-rh-bar-header">
                                <span className="kpi-rh-bar-label">Extras</span>
                                <span className="kpi-rh-bar-pct">5%</span>
                            </div>
                            <div className="kpi-rh-progress-bg">
                                <div className="kpi-rh-progress-fill kpi-bg-orange" style={{ width: '5%' }}></div>
                            </div>
                        </div>
                    </div>

                    {/* Alerte Planning */}
                    <div className="kpi-rh-alert kpi-mt-32">
                        <strong className="kpi-rh-alert-title">ALERTE PLANNING</strong>
                        <p className="kpi-rh-alert-text">
                            Manque 1 extra pour le service de Samedi soir (Soirée Jazz).
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const fetchPerformance = async (period) => {
        setLoadingKpi(true);

        const token = JSON.parse(localStorage.getItem("user"))?.access_token;
        const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;

        if (!token || !ideresto) {
            MySwal.fire({
                icon: "error",
                text: "Session expirée. Veuillez vous reconnecter.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
            });
            setLoadingKpi(false);
            return;
        }

        try {
            const response = await axios.get(
                `${base_api_url}api/commandes/articles/performance`,
                {
                    params: {
                        periode: period,
                        restoId: ideresto
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Performance API:", response.data);

            // ⚠️ Si API retourne vide
            if (!response.data || !response.data.statistiques_globales) {
                MySwal.fire({
                    icon: "info",
                    text: "Aucune donnée trouvée pour cette période.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                });
                setKpiData(null);
                return;
            }

            setKpiData(response.data);

        } catch (error) {

            console.error("Erreur chargement performance:", error);

            MySwal.fire({
                icon: "error",
                text: "Erreur lors du chargement des performances.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
            });

        } finally {
            setLoadingKpi(false);
        }
    };

    const fetchPerformanceProduit = async (year, month) => {
        setLoadingProduits(true);

        const token    = JSON.parse(localStorage.getItem("user"))?.access_token;
        const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;

        if (!token || !ideresto) {
            MySwal.fire({ icon: "error", text: "Session expirée. Veuillez vous reconnecter.", toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000 });
            setLoadingProduits(false);
            return;
        }

        try {
            const response = await axios.get(
                `${base_api_url}api/performances/${ideresto}/${year}/${month}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            console.log("Performance produit API:", response.data);

            if (response.data?.success) {
                setKpiProduitData(response.data);
            } else {
                setKpiProduitData(null);
                MySwal.fire({ icon: "info", text: "Aucune donnée pour cette période.", toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000 });
            }

        } catch (error) {
            console.error("Erreur chargement performance produit:", error);
            setKpiProduitData(null);
            MySwal.fire({ icon: "error", text: "Erreur lors du chargement des performances produit.", toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000 });
        } finally {
            setLoadingProduits(false);
            setLoadingProduitKpi(false);
        }
    };

    useEffect(() => {
        const restaurantData = JSON.parse(localStorage.getItem("restaurant"));
        if (restaurantData?.restaurant?.devise) {
            setDevise(restaurantData.restaurant.devise);
        }
        if (activeTab === "vue_ensemble") {
            fetchPerformance(timeFilter);
        } else if (activeTab === "produits") {
            fetchPerformanceProduit(produitYear, produitMonth);
        }
    }, [activeTab, timeFilter, devise, produitYear, produitMonth]);

    return (
        <div className="page-wrapper">
            <div className="kpi-page">
                {/* Header */}
                <div className="kpi-header">
                    <h1 className="kpi-title">Pilotage & Performance</h1>
                    <p className="kpi-subtitle">Analysez la santé financière et opérationnelle de votre restaurant.</p>
                </div>

                {/* Tabs */}
                <div className="kpi-tabs-container">
                    <div className="kpi-tabs">
                        {tabs.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`kpi-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <Icon size={14} className="kpi-tab-icon" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'vue_ensemble' && renderVueEnsemble()}
                {activeTab === 'finances' && renderFinances()}
                {activeTab === 'produits' && renderProduits()}
                {activeTab === 'rh' && renderRH()}
            </div>

            {/* Modal Rapport de Rentabilité */}
            {showRapport && (
                <>
                    <div className="kpi-modal-backdrop" onClick={() => setShowRapport(false)}></div>
                    <div className="kpi-modal">
                        <div className="kpi-modal-header">
                            <h2 className="kpi-modal-title">Rapport de Rentabilité</h2>
                            <button className="kpi-modal-close" onClick={() => setShowRapport(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="kpi-modal-body">
                            {/* En-tête du rapport */}
                            <div className="kpi-rap-header">
                                <div>
                                    <h3 className="kpi-rap-maintitle">Rapport Hebdomadaire</h3>
                                    <p className="kpi-rap-subtitle">Semaine 06 (05 Fév - 11 Fév 2026)</p>
                                </div>
                                <div className="kpi-rap-meta">
                                    <span className="kpi-rap-badge">LEUK PARIS</span>
                                    <p className="kpi-rap-date">Généré le 12/02/2026</p>
                                </div>
                            </div>

                            {/* Section 1 */}
                            <div className="kpi-rap-section">
                                <h4 className="kpi-rap-section-title">
                                    <span className="kpi-rap-bar"></span>
                                    1. SYNTHÈSE EXECUTIVE (FLASH P&L)
                                </h4>
                                <p className="kpi-rap-text">
                                    Performance globale positive avec un EBE estimé à <span className="kpi-text-green-bold">31%</span> (Obj. 30%). Excellente maîtrise du coût matière.
                                </p>
                                <div className="kpi-rap-stats-grid">
                                    <div className="kpi-rap-stat">
                                        <span className="kpi-rap-stat-label">CA HT</span>
                                        <span className="kpi-rap-stat-val">13 250 €</span>
                                    </div>
                                    <div className="kpi-rap-stat">
                                        <span className="kpi-rap-stat-label">Marge Brute</span>
                                        <span className="kpi-rap-stat-val kpi-text-green-bold">72%</span>
                                    </div>
                                    <div className="kpi-rap-stat">
                                        <span className="kpi-rap-stat-label">Ratio RH</span>
                                        <span className="kpi-rap-stat-val kpi-text-red-bold">31%</span>
                                    </div>
                                    <div className="kpi-rap-stat">
                                        <span className="kpi-rap-stat-label">Prime Cost</span>
                                        <span className="kpi-rap-stat-val">59%</span>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2 */}
                            <div className="kpi-rap-section">
                                <h4 className="kpi-rap-section-title">
                                    <span className="kpi-rap-bar kpi-bar-orange"></span>
                                    2. ANALYSE DES COÛTS
                                </h4>
                                
                                <div className="kpi-rap-alert kpi-rap-alert-success">
                                    <div className="kpi-rap-alert-head">
                                        <strong>Coût Matière : 28%</strong>
                                        <span className="kpi-rap-alert-badge kpi-badge-success">EXCELLENT</span>
                                    </div>
                                    <p className="kpi-rap-alert-text">Objectif budgété : 30%. Économie réalisée grâce à la négo fournisseur &quot;tomates&quot; et gestion des pertes (0.8%).</p>
                                </div>

                                <div className="kpi-rap-alert kpi-rap-alert-danger">
                                    <div className="kpi-rap-alert-head">
                                        <strong>Ratio Personnel : 31%</strong>
                                        <span className="kpi-rap-alert-badge kpi-badge-danger">ATTENTION</span>
                                    </div>
                                    <p className="kpi-rap-alert-text">Dérapage sur les services du soir (fermetures tardives +45min). Sureffectif constaté Samedi soir.</p>
                                </div>
                            </div>

                            {/* Section 3 */}
                            <div className="kpi-rap-section">
                                <h4 className="kpi-rap-section-title">
                                    <span className="kpi-rap-bar kpi-bar-orange-dark"></span>
                                    3. PLAN D&apos;ACTION & RECOMMANDATIONS
                                </h4>
                                <ul className="kpi-rap-list">
                                    <li>
                                        <span className="kpi-dot kpi-dot-red"></span>
                                        <span><strong>Optimisation RH :</strong> Décaler l&apos;arrivée de l&apos;extra du samedi à 19h00 et valider les checklists de pré-clôture.</span>
                                    </li>
                                    <li>
                                        <span className="kpi-dot kpi-dot-green"></span>
                                        <span><strong>Ventes :</strong> Pousser les suggestions &quot;Pavé de Saumon&quot; (stock à écouler) et les ventes additionnelles café le midi.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="kpi-modal-footer">
                            <button className="kpi-btn-light" onClick={() => setShowRapport(false)}>Fermer</button>
                            <button className="kpi-btn-orange">
                                <DownloadCloud size={16} />
                                Télécharger PDF
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MesKpi;
